import React from "react";
import AppHeading from "../layout/parts/app-heading";
import AppBody from "../layout/parts/app-body";
import { useAppContext } from "../providers/app-context.jsx";
import ReactModal from "react-modal";
import Loader from "../common/loader";
import { toast } from "react-toastify";
import axios, { responseHandler, errorHandler } from "../requests/axios";
import LockResetRoundedIcon from "@mui/icons-material/LockResetRounded";

function ChangePasswordDialog({ isOpen, onClose }) {
  const [form, setForm] = React.useState({
    current_password: "",
    new_password: "",
    new_password_confirmation: "",
  });
  const [errors, setErrors] = React.useState({});
  const [isLoading, setLoading] = React.useState(false);

  function handleChange(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  function validateForm() {
    let formErrors = {};

    if (!form.current_password) {
      formErrors.current_password = "Please enter your current password.";
    }

    if (!form.new_password) {
      formErrors.new_password = "Please enter a new password.";
    } else if (form.new_password.length < 8) {
      formErrors.new_password =
        "New password must be at least 8 characters.";
    }

    if (!form.new_password_confirmation) {
      formErrors.new_password_confirmation =
        "Please confirm your new password.";
    } else if (form.new_password !== form.new_password_confirmation) {
      formErrors.new_password_confirmation = "Passwords do not match.";
    }

    return formErrors;
  }

  function formatApiErrors(errorObj) {
    let apiErrors = {};
    for (const key in errorObj) {
      if (errorObj.hasOwnProperty(key)) {
        apiErrors[key] = Array.isArray(errorObj[key])
          ? errorObj[key].join(". ")
          : errorObj[key];
      }
    }
    setErrors(apiErrors);
  }

  function handleSubmit() {
    setErrors({});
    const formErrors = validateForm();

    if (Object.keys(formErrors).length) {
      setErrors(formErrors);
      return;
    }

    setLoading(true);

    axios
      .put("/api/users/change-password", form)
      .then(
        responseHandler(() => {
          toast.success("Password changed successfully.");
          handleClose();
        })
      )
      .catch(
        errorHandler((error) => {
          if (error.status === 422 || error.response?.status === 422) {
            const data = error.data || error.response?.data;
            if (data?.errors) {
              formatApiErrors(data.errors);
            } else {
              toast.error(data?.message || "Validation error.");
            }
          } else {
            toast.error(
              "An error occurred while changing your password. Please try again."
            );
          }
        })
      )
      .finally(() => setLoading(false));
  }

  function handleClose() {
    setForm({
      current_password: "",
      new_password: "",
      new_password_confirmation: "",
    });
    setErrors({});
    setLoading(false);
    onClose();
  }

  return (
    <ReactModal isOpen={isOpen} onRequestClose={handleClose} ariaHideApp={false}>
      <div className="v-react-modal__inner modal-width">
        <div className="v-react-modal__heading">Change Password</div>
        <div className="v-react-modal__body">
          <div className="form-field">
            <label>Current Password</label>
            <input
              type="password"
              name="current_password"
              value={form.current_password}
              placeholder="Enter your current password"
              onChange={(e) => handleChange("current_password", e.target.value)}
            />
            {errors.current_password ? (
              <div className="form-field__error">{errors.current_password}</div>
            ) : null}
          </div>
          <div className="form-field">
            <label>New Password</label>
            <input
              type="password"
              name="new_password"
              value={form.new_password}
              placeholder="Enter your new password"
              onChange={(e) => handleChange("new_password", e.target.value)}
            />
            {errors.new_password ? (
              <div className="form-field__error">{errors.new_password}</div>
            ) : null}
          </div>
          <div className="form-field">
            <label>Confirm New Password</label>
            <input
              type="password"
              name="new_password_confirmation"
              value={form.new_password_confirmation}
              placeholder="Confirm your new password"
              onChange={(e) =>
                handleChange("new_password_confirmation", e.target.value)
              }
            />
            {errors.new_password_confirmation ? (
              <div className="form-field__error">
                {errors.new_password_confirmation}
              </div>
            ) : null}
          </div>
        </div>
        <div className="v-react-modal__footer" style={{ marginTop: "4px" }}>
          {isLoading ? (
            <Loader width={45} height={45} />
          ) : (
            <React.Fragment>
              <button className="btn btn--inverse-primary" onClick={handleClose}>
                Cancel
              </button>
              <button className="btn btn--primary" onClick={handleSubmit}>
                Change Password
              </button>
            </React.Fragment>
          )}
        </div>
      </div>
    </ReactModal>
  );
}

export default function ManageAccountPage() {
  const { user } = useAppContext();
  const [showPasswordDialog, setShowPasswordDialog] = React.useState(false);

  const userData = user?.data;
  const loading = !userData;
  console.log({userData})

  return (
    <React.Fragment>
      <AppHeading
        title="My Account"
        subtitle="Manage your account settings."
      />
      <AppBody loading={loading}>
        <div className="manage-account-page">
          <div className="manage-account-page__section">
            <div className="manage-account-page__section-title">
              Account Information
            </div>
            <div className="manage-account-page__info-grid">
              <div className="manage-account-page__info-item">
                <div className="manage-account-page__info-label">Name</div>
                <div className="manage-account-page__info-value">
                  {userData?.name}
                </div>
              </div>
              <div className="manage-account-page__info-item">
                <div className="manage-account-page__info-label">Email</div>
                <div className="manage-account-page__info-value">
                  {userData?.email}
                </div>
              </div>
              <div className="manage-account-page__info-item">
                <div className="manage-account-page__info-label">
                  Organisation
                </div>
                <div className="manage-account-page__info-value">
                  {userData?.organisation?.organisation_name ?? "-"}
                </div>
              </div>
              <div className="manage-account-page__info-item">
                <div className="manage-account-page__info-label">
                  Password
                </div>
                <div className="manage-account-page__info-value">
                  <button
                      className="btn btn--primary"
                      onClick={() => setShowPasswordDialog(true)}
                  >
                    <LockResetRoundedIcon
                        style={{ fontSize: "18px", marginRight: "6px" }}
                    />
                    Change Password
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </AppBody>

      <ChangePasswordDialog
        isOpen={showPasswordDialog}
        onClose={() => setShowPasswordDialog(false)}
      />
    </React.Fragment>
  );
}
